import React from 'react';
import PropTypes from 'prop-types';

import jwt from 'jwt-decode';

import { 
  BrowserRouter, 
  Route,
}                     from 'react-router-dom';

import {
  default as Amplify,
  Auth,
  Hub,
  Logger,
}                     from 'aws-amplify';

import {
  Fab,
  Toolbar,
  Zoom,
}                     from '@material-ui/core';

import { 
  createMuiTheme, 
  makeStyles,
  responsiveFontSizes, 
  ThemeProvider,
}                     from '@material-ui/core/styles';

import {
  Timer as TimerIcon 
}                     from '@material-ui/icons';

import {
  ConsoleAppBar,
  ConsoleSideMenu,
  LandingPage,
  SessionDialog,
}                     from './components/console';

import { 
  UserProvider,
  useAuthorization,
}                     from './components/user';

import {
  ResourcesProvider,
  useResources,
}                     from './components/resource';

import {
  Executions,
}                     from './components/execution';

import { 
  Account,
  Catalog,
  Clients,
  Credentials,
  Dashboard,
  Datasets,
  Execution,
  ExecutionInput,
  Datastores,
  LandingZones,
  Resource,
  Resources,
  Streams,
  Subscriptions,
  Workloads,
}                     from './pages';

import useLocalStorageState from 'use-local-storage-state';

import { 
  startKeepAlive, 
  startTimeRemaining 
} from './components/utils/CognitoKeepAlive';

import amplify_config from './amplify_config';

const logger = new Logger('Console');

Amplify.configure(amplify_config);

let theme = createMuiTheme({
  palette: {
    primary: {
      main: '#0056A8',
    },
  }
});
theme = responsiveFontSizes(theme);
theme.typography.h4 = {
  fontSize: '1.2rem',
  '@media (min-width:600px)': {
    fontSize: '1.5rem',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '2.125rem',
  },
};
theme.typography.h5 = {
  fontSize: '1.125rem',
  '@media (min-width:600px)': {
    fontSize: '1.2rem',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '1.5rem',
  },
};

const useDelayedRender = delay => {
  const [delayed, setDelayed] = React.useState(true);
  React.useEffect(() => {
    const timeout = setTimeout(() => setDelayed(false), delay);
    return () => clearTimeout(timeout);
  }, [delay]);
  return fn => !delayed && fn();
};

const useStyles = makeStyles((theme) => ({
  content: {
    //flexGrow: 1,
    //padding: theme.spacing(3),
  },
  fab: {
    position: 'fixed',
    margin: theme.spacing(1),
    bottom: theme.spacing(2),
    right: theme.spacing(2),
    paddingRight: '16px',
  },
  fabIcon: {
    marginRight: theme.spacing(1),
  },
}));

function fmtSelectedClientData(client) {
  return {
    id: client.ClientAccountId,
    name: client.ClientAccountAlias ? client.ClientAccountAlias : client.ClientAccountId,
    resource: client
  };
}

const DelayedRender = ({ delay, children }) => useDelayedRender(delay)(() => children);

if( window.location.href.includes('&refresh_token=')) {
  try {
    const claims=jwt(window.location.href.split('id_token=')[1].split('&')[0]);
    window.localStorage.setItem(
      `pac.${claims.aud}.${claims["cognito:username"]}.refreshToken`, 
      window.location.href.split('refresh_token=')[1].split('&')[0]);
    console.log("enabled refresh keep-alive");
  } catch(err) {
    console.log("WARNING: cannot enable refresh keep-alive");
  }
}

export default function Console(props) {

  const classes = useStyles();
  const basePrefId = "console_";

  const [user, setUser] = React.useState(null);
  const [secondsRemaining, setSecondsRemaining] = React.useState(-1);
  const [sideMenuOpen, setSideMenuOpen] = React.useState(false);
  const [sessionDialogOpen, setSessionDialogOpen] = React.useState(false);
  const [selectedClient, setSelectedClient] = useLocalStorageState(basePrefId+"_sc", {});
  const [logLevel, setLogLevel] = useLocalStorageState(basePrefId+"_loglvl",'INFO');

  const { authorization, loading: authLoading, error: authError } = 
    useAuthorization(user?user.signInUserSession.idToken.jwtToken:null);
  const usedResources = useResources({ 
    dataLakeRole: authorization?.DataLakeRole, clientAccountId: selectedClient.id, subscribed: 'all' });

  const transitionDuration = {
    enter: theme.transitions.duration.enteringScreen,
    exit: theme.transitions.duration.leavingScreen,
  };

  const handleSessionTimeRemaining = (seconds) => {
    if (seconds<300) {
      logger.info(`session time left ${seconds}`);  
      setSecondsRemaining(seconds);
      setSessionDialogOpen(seconds<=0);
    }
  };

  React.useEffect(() => {
    console.log("LOGLEVEL: ",logLevel);
    Amplify.Logger.LOG_LEVEL = logLevel;
  }, [logLevel]);
  
  React.useEffect(() => {
    //logger.info("DEFAULT CLIENT USE EFFECT:",authorization, selectedClient);
    if(authorization) {
      // if the user has no DL role but is authorized to see at least one client 
      if(['Admin','Auditor'].indexOf(authorization.DataLakeRole)<0 && authorization.Clients && authorization.Clients.length>0) {
        
        // and if no selectedClient has been set or one has but is no longer authorized...
        if( !selectedClient.id || 
            (selectedClient.id && !authorization.Clients.some(e=>e.ClientAccountId === selectedClient.id) )) {
        
          // then go ahead and set an initial default for this to first client in the list
          logger.info("INITIALIZING SELECTED CLIENT: ",authorization.Clients[0]);
          setSelectedClient(fmtSelectedClientData(authorization.Clients[0]));
        }
      }
    }
  }, [authorization, setSelectedClient, selectedClient.id]);

  React.useEffect(() => {
    
    const updateUser = async authState => {
      
      // const { payload } = authState;
      // const { event, data, message } = payload;
      
      logger.info("AUTHSTATE: ",authState);
      
      try {
        const user = await Auth.currentAuthenticatedUser();
        setUser(user);
        try {
          const pacRefreshTokenKey = `pac.${user.pool.getClientId()}.${user.username}.refreshToken`;
          const pacRefreshToken = window.localStorage.getItem(pacRefreshTokenKey);
          if(pacRefreshToken) {
            const refreshTokenKey = `CognitoIdentityServiceProvider.${user.pool.getClientId()}.${user.username}.refreshToken`;
            user.storage.setItem(refreshTokenKey, pacRefreshToken);
            logger.debug("found PAC refresh token, enabling keep-alive...");
            startKeepAlive(15);
          } else {
            logger.debug(`no PAC refresh token '${pacRefreshTokenKey}'`);
          }
          startTimeRemaining(handleSessionTimeRemaining, 30);
        } catch(e) {
          logger.warning("unable to set cognito refresh token");
        }
      } catch(e) {
        logger.debug(e);
        setUser(null);
      }
    };
    
    Hub.listen('auth', updateUser);
    updateUser();
    
    return () => Hub.remove('auth',updateUser);
  }, []);

  const handleSelectClient = (clientAccountId) => {
    let selectedClient={};
    if(authorization) {
      authorization.Clients.forEach((client) => {
        if(client.ClientAccountId===clientAccountId) {
          selectedClient=fmtSelectedClientData(client);
        }
      });
    }
    setSelectedClient(selectedClient);
    logger.info("SELECTED CLENT: ",selectedClient);
  };
  
  const handleSideMenuClose = () => {
    setSideMenuOpen(false);
  };
  const handleSideMenuOpen = () => {
    setSideMenuOpen(true);
  };

  let userContext = null;

  if(user) {
    
    if(!authorization) {
      if(authError) {
        //logger.error("AUTHORIZATION FAILED: ",authError);
        return(<LandingPage />);
      } else if(authLoading) {
        logger.debug("Authorizing...");
      } else {
        logger.debug("Waiting to authorize...");
      }
      return(null);
    }
    
    if(usedResources.error) {
      logger.error("FAILED loading resources: ",usedResources.error);
      return(null);
    }
  
    userContext = {
      username: user.attributes.name,
      role: authorization.DataLakeRole,
      email: user.attributes.email,
      phone: user.attributes.phone_number,
      fullname: user.attributes.given_name + " " + user.attributes.family_name,
      selectedClient: selectedClient?selectedClient.id:null,
      // authorizedClients: authorization.Clients,
      clients: authorization.Clients.reduce((map,client)=>((map[client.ClientAccountId]=client,map)),{}),
      amplifyData: user,
    };

    logger.debug("userContext: ",userContext);

  }
  
  // enrich client resources with owned resource count
  const clientResourceCounts={};
  if(usedResources.resources && !usedResources.loading) {
    usedResources.resources.forEach( (resource) => {
      const id=resource.ClientAccountId;
      if(resource.ResourceType!=='Client') {
        clientResourceCounts[id]=(clientResourceCounts[id] || 0)+1;
      }
    });
  }
  usedResources.resources=usedResources.resources.map((resource)=>{
    if(resource.ResourceType==='Client') {
      return({...resource, 'OwnedResources': clientResourceCounts[resource.ClientAccountId] || 0});
    } else {
      return(resource);
    }
  });
  
  const minutesRemaining=Math.max(1,Math.round(secondsRemaining/60));
  
  return (
    <BrowserRouter>
        <ThemeProvider theme={theme}>
          { user ?
            <UserProvider value={userContext}>
              <ResourcesProvider value={usedResources}>
                <ConsoleAppBar 
                  user={userContext}
                  selectedClientId={selectedClient.id}
                  onNavMenuClick={handleSideMenuOpen}
                  onSelectClient={handleSelectClient}
                />
                <ConsoleSideMenu resources={usedResources.resources} open={sideMenuOpen} onClose={handleSideMenuClose}/>
                <Toolbar style={{ minHeight: '69px' }}/>
                <main className={classes.content}>
                  <Route path={["/", "/dashboard"]} exact component={Dashboard}/>
                  <Route path="/account" exact component={Account}/>
                  <Route path="/catalog" exact component={Catalog}/>
                  <Route path="/executions" exact component={Executions}/>
                  <Route path="/executions/:id" exact component={Execution}/>
                  <Route path="/executions/:id/:encodedInputUri" exact component={ExecutionInput}/>
                  <Route path="/resources/:id" exact component={Resource}/>
                  <Route path="/resources" exact component={Resources}/>
                  <Route path="/resources/clients" exact component={Clients}/>
                  <Route path="/resources/clients/:id" exact component={Resource}/>
                  <Route path="/resources/credentials" exact component={Credentials}/>
                  <Route path="/resources/credentials/:id" exact component={Resource}/>
                  <Route path="/resources/datasets" exact component={Datasets}/>
                  <Route path="/resources/datasets/:id" exact component={Resource}/>
                  <Route path="/resources/datastores" exact component={Datastores}/>
                  <Route path="/resources/datastores/:id" exact component={Resource}/>
                  <Route path="/resources/landingZones" exact component={LandingZones}/>
                  <Route path="/resources/landingZones/:id" exact component={Resource}/>
                  <Route path="/resources/streams" exact component={Streams}/>
                  <Route path="/resources/streams/:id" exact component={Resource}/>
                  <Route path="/resources/subscriptions" exact component={Subscriptions}/>
                  <Route path="/resources/subscriptions/:id" exact component={Resource}/>
                  <Route path="/resources/workloads" exact component={Workloads}/>
                  <Route path="/resources/workloads/:id" exact component={Resource}/>
                </main>
                <Zoom in={secondsRemaining>0 && secondsRemaining<45} timeout={transitionDuration} unmountOnExit>
                  <Fab
                      className={classes.fab}
                      variant="extended"
                      size="small"
                      color="secondary"
                      aria-label="session timeout"
                    >
                      <TimerIcon className={classes.fabIcon}/>
                      {secondsRemaining<60?"<":""}{minutesRemaining} min{minutesRemaining===1?"":"s"} left in session
                  </Fab>
                </Zoom>
                <SessionDialog open={sessionDialogOpen} onClose={() => setSessionDialogOpen(false)} />
              </ResourcesProvider>
            </UserProvider>
            :
            <DelayedRender delay={1500}>
              <LandingPage/>
            </DelayedRender>
          }
        </ThemeProvider>
    </BrowserRouter>

  );  
}

Console.propTypes = {
  user: PropTypes.object,
};

Console.defaultProps = {
};

